import React from "react";
import { Tabs, Tab, TabsProps, TabProps } from "@mui/material";
import clsx from "clsx";

/**
 * Allowed primitive types for tab values.
 */
export type TabValue = string | number;

/**
 * A single tab item. Reuses MUI's TabProps types for label/icon.
 */
export interface TabItem<V extends TabValue = string> {
  label: TabProps["label"];
  value: V;
  icon?: TabProps["icon"];
  className?: string;
  disabled?: boolean;
  /**
   * Extra props forwarded to the underlying MUI <Tab />.
   * We omit props that the wrapper owns to avoid accidental overrides.
   */
  tabProps?: Omit<
    TabProps,
    "label" | "value" | "icon" | "disabled" | "className"
  >;
}

/**
 * Allow either a simple (v) => void callback or a React state setter (setState).
 * The union makes it ergonomic to pass setState directly (onChange={setActiveTab}).
 */
type StateSetter<V> = React.Dispatch<React.SetStateAction<V>>;
type OnChangeHandler<V> = ((value: V) => void) | StateSetter<V>;

export interface TabsWrapperProps<V extends TabValue = string>
  extends Omit<TabsProps, "onChange" | "value"> {
  tabs: TabItem<V>[];
  value: V;
  onChange: OnChangeHandler<V>;
  className?: string;
}

/**
 * Generic Tabs wrapper.
 *
 * Notes:
 * - `V` is the tab value type (string | number). Pass a union type for strict typing.
 * - Internally we cast the handler to call it because TypeScript can't express both
 *   callback shapes perfectly at the invocation site. This is a safe internal cast.
 */
const TabsWrapper = <V extends TabValue = string>({
  tabs,
  value,
  onChange,
  className,
  ...muiTabsProps
}: TabsWrapperProps<V>) => {
  const handleChange: TabsProps["onChange"] = (_event, newValue) => {
    const v = newValue as V;
    // We use a narrow call here. onChange can be either a setter or a callback.
    // Casting to `any` is an internal implementation detail to make the wrapper ergonomic.
    // Callers still get full typing when passing either a (v) => void or setState.
    (onChange as any)(v);
  };

  return (
    <Tabs
      value={value}
      onChange={handleChange}
      className={clsx("p-0 m-0 border-b border-gray-200", className)}
      {...muiTabsProps}
    >
      {tabs.map((t) => (
        <Tab
          key={String(t.value)}
          value={t.value}
          label={t.label}
          icon={t.icon}
          disabled={t.disabled}
          className={clsx("normal-case text-sm min-h-[40px]", t.className)}
          {...t.tabProps}
        />
      ))}
    </Tabs>
  );
};

export default TabsWrapper;
